import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import moment from 'moment';
import { useStores } from 'Hooks/useStore';
import { AssignmentSource } from 'Services/Api/AssignmentSources/Types';
import {
	assignmentsForCourse,
	certificationLabel
} from 'LearningManager/helpers/assignment';
import { formatCardDate, formatRunTime } from 'LearningManager/helpers/date';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import useLanguageNames from 'LearningManager/hooks/useLanguageNames';
import styles from './CourseDetailsDrawer.module.scss';

export interface CourseDrawerItem {
	id: number;
	title: string;
	image: string;
	tags: string[];
	status?: 'Completed' | 'In Progress' | 'Not Started';
}

interface CourseDetailsDrawerProps {
	visible: boolean;
	course: CourseDrawerItem | null;
	onClose: () => void;
}

interface AssignmentOption {
	id: number;
	label: string;
}

const NOT_AVAILABLE = '-';

const dueLabel = (source: AssignmentSource): string =>
	source.due_date ? formatCardDate(source.due_date) : 'None';

const typeLabel = (source: AssignmentSource): string =>
	source.is_assigned_to_future_users ? 'Ongoing' : 'One-Time';

// An assignment whose start date has not arrived cannot have been started;
// anything past it needs learner progress the API does not return yet.
const isNotStarted = (source: AssignmentSource): boolean =>
	Boolean(source.start_date) &&
	moment(source.start_date).isAfter(moment(), 'day');

interface DetailLineProps {
	label: string;
	value: string;
}

const DetailLine: React.FC<DetailLineProps> = ({ label, value }) => (
	<div className={styles.detailLine}>
		<span className={styles.detailLine__label}>{label}:</span>{' '}
		<span className={styles.detailLine__value}>{value}</span>
	</div>
);

const CourseDetailsDrawer: React.FC<CourseDetailsDrawerProps> = observer(
    ({ visible, course, onClose }) => {
        const { t } = useTranslation();
        const { learningManagerStore } = useStores();
        const getLanguageNames = useLanguageNames();

        const [loading, setLoading] = useState(false);
        const [selectedAssignmentIds, setSelectedAssignmentIds] =
            useState<number[] | null>(null);

        useEffect(() => {
            if (!visible || !course) return;
            if (learningManagerStore.coursesById[course.id]) return;

            setLoading(true);
            learningManagerStore
                .fetchCourseById(course.id)
                .finally(() => setLoading(false));
        }, [visible, course?.id]);

        const detail = course
            ? learningManagerStore.coursesById[course.id]
            : undefined;

        const { assignmentSources, learningPathsById } =
            learningManagerStore;

        const courseAssignments = useMemo<AssignmentSource[]>(
            () =>
                course
                    ? assignmentsForCourse(
                        assignmentSources,
                        learningPathsById,
                        course.id
                    )
                    : [],
            [course?.id, assignmentSources, learningPathsById]
        );

        const assignmentOptions = useMemo<AssignmentOption[]>(
            () =>
                courseAssignments.map((source) => ({
                    id: source.id,
                    label: source.name
                })),
            [courseAssignments]
        );

        const dropdownKey = `${course?.id ?? 0}:${assignmentOptions
            .map((option) => option.id)
            .join()}`;

        useEffect(() => {
            setSelectedAssignmentIds(null);
        }, [dropdownKey]);

        const effectiveSelectedIds =
            selectedAssignmentIds ??
            assignmentOptions.map((option) => option.id);

        const selectedAssignments = courseAssignments.filter((source) =>
            effectiveSelectedIds.includes(source.id)
        );

        const languages = getLanguageNames(detail?.language_ids);

        const statusKeys: Record<string, string> = {
            Completed: 'learning.courseDetails.status.completed',
            'In Progress': 'learning.courseDetails.status.inProgress',
            'Not Started': 'learning.courseDetails.status.notStarted'
        };

        return (
            <Drawer
                className={styles.courseDetailsDrawer}
                placement="right"
                visible={visible}
                width={420}
                onClose={onClose}
                title={
                    <div className={styles.drawerTitle}>
                        <div className={styles.drawerTitle__name}>
                            {detail?.name ??
                                course?.title ??
                                t('learning.courseDetails.fallbackTitle')}
                        </div>

                        <div className={styles.drawerTitle__badges}>
                            {detail?.is_required && (
                                <span
                                    className={classNames(
                                        styles.drawerTag,
                                        styles['drawerTag--ongoing']
                                    )}
                                >
                                    {t(
                                        'learning.courseDetails.badges.required'
                                    )}
                                </span>
                            )}

                            {course?.status && (
                                <span
                                    className={classNames(styles.drawerTag, {
                                        [styles['drawerTag--completed']]:
                                            course.status === 'Completed',
                                        [styles['drawerTag--inProgress']]:
                                            course.status === 'In Progress',
                                        [styles['drawerTag--notStarted']]:
                                            course.status === 'Not Started'
                                    })}
                                >
                                    {t(
                                        statusKeys[course.status] ??
                                            'learning.courseDetails.fallbackStatus',
                                        { defaultValue: course.status }
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                }
            >
                {loading ? (
                    <div className={styles.drawerLoading}>
                        <Spin />
                    </div>
                ) : (
                    <>
                        {assignmentOptions.length > 1 && (
                            <div className={styles.assignmentSelect}>
                                <div
                                    className={
                                        styles.assignmentSelect__label
                                    }
                                >
                                    {t(
                                        'learning.courseDetails.assignment.label'
                                    )}
                                </div>

                                <MultiSelectDropdown
                                    key={dropdownKey}
                                    options={assignmentOptions}
                                    defaultSelectedIds={effectiveSelectedIds}
                                    onChange={setSelectedAssignmentIds}
                                    allLabel={t(
                                        'learning.courseDetails.assignment.all'
                                    )}
                                />
                            </div>
                        )}

                        {selectedAssignments.length === 0 ? (
                            <div className={styles.drawerSection}>
                                <div className={styles.detailLine}>
                                    {t(
                                        'learning.courseDetails.assignment.noneSelected'
                                    )}
                                </div>
                            </div>
                        ) : (
                            selectedAssignments.map((source) => (
                                <div
                                    key={source.id}
                                    className={styles.assignmentBlock}
                                >
                                    <div
                                        className={
                                            styles.assignmentBlock__header
                                        }
                                    >
                                        <div
                                            className={
                                                styles.assignmentBlock__name
                                            }
                                        >
                                            {t(
                                                'learning.courseDetails.assignment.name',
                                                { name: source.name }
                                            )}
                                        </div>

                                        <div
                                            className={
                                                styles.assignmentBlock__badges
                                            }
                                        >
                                            <span
                                                className={classNames(
                                                    styles.drawerTag,
                                                    source.is_assigned_to_future_users
                                                        ? styles[
                                                            'drawerTag--ongoing'
                                                        ]
                                                        : styles[
                                                            'drawerTag--oneTime'
                                                        ]
                                                )}
                                            >
                                                {typeLabel(source)}
                                            </span>

                                            {isNotStarted(source) && (
                                                <span
                                                    className={classNames(
                                                        styles.drawerTag,
                                                        styles[
                                                            'drawerTag--notStarted'
                                                        ]
                                                    )}
                                                >
                                                    {t(
                                                        'learning.courseDetails.assignment.notStarted'
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.drawerSection}>
                                        <div
                                            className={
                                                styles.drawerSection__heading
                                            }
                                        >
                                            {t(
                                                'learning.courseDetails.sections.details'
                                            )}
                                        </div>

                                        <div className={styles.subSection}>
                                            <DetailLine
                                                label={t(
                                                    'learning.courseDetails.fields.created'
                                                )}
                                                value={source?.created_at ? formatCardDate(source?.created_at) : NOT_AVAILABLE}
                                            />
                                            <DetailLine
                                                label={t(
                                                    'learning.courseDetails.fields.updated'
                                                )}
                                                value={source?.updated_date ? formatCardDate(source?.updated_date) : NOT_AVAILABLE}
                                            />
                                            <DetailLine
                                                label={t(
                                                    'learning.courseDetails.fields.start'
                                                )}
                                                value={source?.start_date ? formatCardDate(source?.start_date) : NOT_AVAILABLE}
                                            />
                                            <DetailLine
                                                label={t(
                                                    'learning.courseDetails.fields.duration'
                                                )}
                                                value={source?.run_time ? formatRunTime(source?.run_time) : NOT_AVAILABLE}
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.drawerSection}>
                                        <div
                                            className={
                                                styles.drawerSection__heading
                                            }
                                        >
                                            {t(
                                                'learning.courseDetails.sections.assignment'
                                            )}
                                        </div>

                                        <div className={styles.subSection}>
                                            {source.scopes.length ? (
                                                source.scopes.map((scope) => (
                                                    <div key={scope} className={styles.detailLine}>
                                                        {t(
                                                            `learning.viewDetails.scopes.${scope}`,
                                                            { defaultValue: scope }
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className={styles.detailLine}>
                                                    {NOT_AVAILABLE}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.drawerSection}>
                                        <div
                                            className={
                                                styles.drawerSection__heading
                                            }
                                        >
                                            {t(
                                                'learning.courseDetails.sections.scheduling'
                                            )}
                                        </div>

                                        <div className={styles.subSection}>
                                            <DetailLine
                                                label={t(
                                                    'learning.courseDetails.fields.due'
                                                )}
                                                value={dueLabel(source)}
                                            />
                                            <DetailLine
                                                label={t(
                                                    'learning.courseDetails.fields.certificationExpiration'
                                                )}
                                                value={certificationLabel(
                                                    source
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}

                        <div
                            className={classNames(
                                styles.drawerSection,
                                styles.languagesSection
                            )}
                        >
                            <div
                                className={styles.drawerSection__heading}
                            >
                                {t(
                                    'learning.courseDetails.sections.languages'
                                )}
                            </div>

                            {languages.length ? (
                                <div className={styles.languageWrap}>
                                    {languages.map((language) => (
                                        <span
                                            key={language}
                                            className={styles.languageTag}
                                        >
                                            {language}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.subSection}>
                                    <div className={styles.detailLine}>
                                        {NOT_AVAILABLE}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Drawer>
        );
    }
)

export default CourseDetailsDrawer;
